import { useState, useEffect, useMemo, useRef } from 'react';
import { adminApi, type AdminOrder } from '@/lib/admin';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState } from '@/pages/admin/components/UI';

const orderStatusMaps = {
  open: { label: '持仓中', className: 'bg-gold-100 text-gold-700', icon: 'ri-lock-line' },
  closed: { label: '已平仓', className: 'bg-brown-100 text-brown-600', icon: 'ri-check-double-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const directionMaps: Record<string, { label: string; className: string }> = {
  IN: { label: '买入 IN', className: 'bg-red-50 text-red-500' },
  OUT: { label: '卖出 OUT', className: 'bg-emerald-50 text-emerald-600' },
};

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'open', label: '持仓中' },
  { key: 'closed', label: '已平仓' },
];

const directionOptions = [
  { key: 'all', label: '全部方向' },
  { key: 'IN', label: '买入 IN' },
  { key: 'OUT', label: '卖出 OUT' },
];

const statusOptions = [
  { key: 'all', label: '全部状态' },
  { key: 'open', label: '持仓中' },
  { key: 'closed', label: '已平仓' },
];

function formatDateInput(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayStr(): string {
  return formatDateInput(new Date());
}

function getWeekStartStr(): string {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());
  start.setHours(0, 0, 0, 0);
  return formatDateInput(start);
}

function getMonthStartStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function DropdownFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const panelWidth = 140;
      let left = rect.left;
      if (left + panelWidth > window.innerWidth - 16) {
        left = window.innerWidth - panelWidth - 16;
      }
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: Math.max(16, left),
        zIndex: 50,
      });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const selected = options.find((o) => o.key === value) || options[0];

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gold-100 text-gold-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-200 transition-colors min-w-[90px]"
      >
        <span className="whitespace-nowrap">{selected.label}</span>
        <i className={`ri-arrow-down-s-line shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          ref={panelRef}
          style={panelStyle}
          className="bg-white rounded-lg shadow-lg border border-brown-100 overflow-hidden min-w-[120px]"
        >
          {options.map((o) => (
            <button
              key={o.key}
              onClick={() => {
                onChange(o.key);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm cursor-pointer hover:bg-brown-50 transition-colors whitespace-nowrap ${
                value === o.key ? 'text-gold-700 font-medium bg-gold-50/60' : 'text-brown-700'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function DateRangeFilter({
  startDate,
  endDate,
  onChange,
}: {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const panelWidth = 280;
      let left = rect.left;
      if (left + panelWidth > window.innerWidth - 16) {
        left = window.innerWidth - panelWidth - 16;
      }
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: Math.max(16, left),
        zIndex: 50,
      });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const quickOptions = [
    { key: 'today', label: '今天', start: getTodayStr(), end: getTodayStr() },
    { key: 'week', label: '本周', start: getWeekStartStr(), end: getTodayStr() },
    { key: 'month', label: '本月', start: getMonthStartStr(), end: getTodayStr() },
  ];

  const activeQuick = quickOptions.find(
    (q) => q.start === startDate && q.end === endDate
  );

  const displayLabel = activeQuick
    ? activeQuick.label
    : startDate && endDate
      ? `${startDate.replace(/-/g, '.')} - ${endDate.replace(/-/g, '.')}`
      : '全部';

  const handleReset = () => {
    onChange('', '');
    setOpen(false);
  };

  const handleQuick = (start: string, end: string) => {
    onChange(start, end);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gold-100 text-gold-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-200 transition-colors min-w-[90px]"
      >
        <i className="ri-calendar-line shrink-0" />
        <span className="whitespace-nowrap">{displayLabel}</span>
        <i className={`ri-arrow-down-s-line shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          ref={panelRef}
          style={panelStyle}
          className="bg-white rounded-lg shadow-lg border border-brown-100 overflow-hidden w-[280px] p-4"
        >
          {/* 快速选项 */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {quickOptions.map((q) => (
              <button
                key={q.key}
                onClick={() => handleQuick(q.start, q.end)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  activeQuick?.key === q.key
                    ? 'bg-gold-500 text-white'
                    : 'bg-brown-50 text-brown-600 hover:bg-brown-100'
                }`}
              >
                {q.label}
              </button>
            ))}
            <button
              onClick={handleReset}
              className={`px-2.5 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                !startDate && !endDate
                  ? 'bg-gold-500 text-white'
                  : 'bg-brown-50 text-brown-600 hover:bg-brown-100'
              }`}
            >
              全部
            </button>
          </div>

          {/* 自定义日期 */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-brown-500 mb-1 block">开始日期</label>
              <input
                type="date"
                value={startDate}
                max={endDate || getTodayStr()}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(val, endDate || val);
                }}
                className="w-full px-3 py-2 rounded-lg bg-brown-50 border border-brown-100 text-brown-900 text-sm focus:outline-none focus:border-gold-300 focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="text-xs text-brown-500 mb-1 block">结束日期</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={getTodayStr()}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(startDate || val, val);
                }}
                className="w-full px-3 py-2 rounded-lg bg-brown-50 border border-brown-100 text-brown-900 text-sm focus:outline-none focus:border-gold-300 focus:bg-white transition-colors"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('all');
  const [startDate, setStartDate] = useState(getMonthStartStr());
  const [endDate, setEndDate] = useState(getTodayStr());
  const [directionFilter, setDirectionFilter] = useState('all');
  const [settling, setSettling] = useState(false);
  const [settleResult, setSettleResult] = useState<{ settled: number; total: number; error?: string } | null>(null);
  const [searchText, setSearchText] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listOrders();
      setOrders(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAutoSettle = async () => {
    if (settling) return;
    setSettling(true);
    setSettleResult(null);
    try {
      const data = await adminApi.runAutoSettle();
      setSettleResult({ settled: data?.settled || 0, total: data?.total || 0 });
      if ((data?.settled || 0) > 0) {
        await fetchData();
      }
    } catch (err) {
      setSettleResult({
        settled: 0,
        total: 0,
        error: err instanceof Error ? err.message : '结算失败，请稍后重试',
      });
    } finally {
      setSettling(false);
    }
  };

  const tabCounts = useMemo(() => {
    const open = orders.filter((o) => o.status === 'open').length;
    const closed = orders.filter((o) => o.status === 'closed').length;
    return { all: orders.length, open, closed };
  }, [orders]);

  const filtered = useMemo(() => {
    let result = orders;

    const keyword = searchText.trim().toLowerCase();
    if (keyword) {
      result = result.filter((o) => {
        const name = (o.user_name || '').toLowerCase();
        const phone = (o.phone || '').toLowerCase();
        return name.includes(keyword) || phone.includes(keyword);
      });
    }

    if (tab !== 'all') {
      result = result.filter((o) => o.status === tab);
    }

    if (directionFilter !== 'all') {
      result = result.filter((o) => o.direction === directionFilter);
    }

    if (startDate || endDate) {
      result = result.filter((o) => {
        if (!o.created_at) return false;
        const d = new Date(o.created_at);
        const dateStr = formatDateInput(d);
        if (startDate && dateStr < startDate) return false;
        if (endDate && dateStr > endDate) return false;
        return true;
      });
    }

    return result;
  }, [orders, tab, directionFilter, startDate, endDate, searchText]);

  const totalProfit = orders.reduce((s, o) => s + (o.profit || 0), 0);
  const expiredCount = orders.filter((o) => o.status === 'open' && o.lock_expire_at && new Date(o.lock_expire_at) <= new Date()).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">订单管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">
            共 {orders.length} 笔订单 · 累计盈亏 ¥{totalProfit.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
            {expiredCount > 0 && (
              <span className="ml-2 text-red-500 font-medium">· {expiredCount} 笔已到期待结算</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoSettle}
            disabled={settling}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              expiredCount > 0
                ? 'bg-red-500 text-white hover:bg-red-600 active:scale-95'
                : 'bg-brown-100 text-brown-600 hover:bg-brown-200'
            } ${settling ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {settling && <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {settling ? '结算中' : expiredCount > 0 ? `结算 ${expiredCount} 笔到期订单` : '执行到期结算'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-brown-100 overflow-hidden">
        <div className="flex items-center justify-center border-b border-brown-100">
          {tabs.map((t) => {
            const count = tabCounts[t.key as keyof typeof tabCounts] || 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`relative flex items-center gap-1.5 px-5 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  active ? 'text-gold-700' : 'text-brown-400 hover:text-brown-600'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    active ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500'
                  }`}>
                    {count}
                  </span>
                )}
                {active && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-gold-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto">
          <div className="relative flex items-center shrink-0">
            <i className="ri-search-line absolute left-3 text-brown-400 text-sm pointer-events-none" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="搜索客户姓名 / 手机号"
              className="pl-9 pr-3 py-2 rounded-lg bg-brown-50 border border-brown-100 text-brown-900 text-sm w-56 focus:outline-none focus:border-gold-300 focus:bg-white transition-colors"
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                className="absolute right-2 w-5 h-5 flex items-center justify-center text-brown-300 hover:text-brown-500 cursor-pointer"
              >
                <i className="ri-close-circle-fill text-sm" />
              </button>
            )}
          </div>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
          <DropdownFilter
            label="方向"
            options={directionOptions}
            value={directionFilter}
            onChange={setDirectionFilter}
          />
        </div>
      </div>

      {settleResult && (
        <div className={`rounded-lg px-4 py-2.5 text-sm font-medium ${
          settleResult.error
            ? 'bg-red-50 text-red-600'
            : settleResult.settled > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
        }`}>
          {settleResult.error
            ? settleResult.error
            : settleResult.settled > 0
              ? `成功结算 ${settleResult.settled} / ${settleResult.total} 笔到期订单`
              : '暂无到期订单需要结算'}
        </div>
      )}

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无订单" desc="该筛选条件下没有订单记录" />
        ) : (
          <div className="overflow-x-auto -mx-4 md:-mx-5">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">订单号</th>
                  <th className="px-4 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium">品种</th>
                  <th className="px-4 py-3 font-medium">方向</th>
                  <th className="px-4 py-3 font-medium">克重</th>
                  <th className="px-4 py-3 font-medium">开仓价</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium">锁价到期</th>
                  <th className="px-4 py-3 font-medium">下单时间</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const dir = directionMaps[o.direction] || directionMaps.IN;
                  const isExpired = o.status === 'open' && o.lock_expire_at && new Date(o.lock_expire_at) <= new Date();
                  return (
                    <tr key={o.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                      <td className="px-4 md:px-5 py-3.5">
                        <p className="text-brown-900 font-medium whitespace-nowrap">{o.id}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="text-brown-700 text-sm font-medium whitespace-nowrap">{o.user_name || '—'}</p>
                        <p className="text-brown-400 text-xs mt-0.5">{o.phone || '—'}</p>
                      </td>
                      <td className="px-4 py-3.5 text-brown-700 font-medium whitespace-nowrap">
                        {o.name || '—'}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${dir.className}`}>
                          {dir.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-brown-700 font-medium">
                        {Number(o.weight).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                      </td>
                      <td className="px-4 py-3.5 text-brown-700">{Number(o.open_price).toFixed(2)}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={o.status} maps={orderStatusMaps} />
                          {isExpired && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium whitespace-nowrap">
                              已到期
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-brown-500 whitespace-nowrap">
                        {o.lock_expire_at
                          ? new Date(o.lock_expire_at).toLocaleString('zh-CN', {
                              month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                            }).replace(/\//g, '-')
                          : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-brown-500 whitespace-nowrap">{o.create_time || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}